import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import {
  DATA_TYPE_OPERATIONS,
  FilterDataType,
  FilterOperation,
  FilterOperationConfig
} from './filter.models';

@Injectable({
  providedIn: 'root'
})
export class FilterOperationsService {
  private translate = inject(TranslateService);

  /**
   * Get all available operations for a specific data type
   */
  getOperationsForDataType(dataType: FilterDataType): FilterOperation[] {
    return DATA_TYPE_OPERATIONS[dataType] || [];
  }

  /**
   * Get operation configuration with translated labels
   */
  getOperationConfig(operation: FilterOperation): FilterOperationConfig {
    const configs: Record<FilterOperation, Omit<FilterOperationConfig, 'label'>> = {
      // Text operations
      [FilterOperation.EQUALS]: {
        operation: FilterOperation.EQUALS,
        requiresValue: true,
        icon: ICONS.equals
      },
      [FilterOperation.NOT_EQUALS]: {
        operation: FilterOperation.NOT_EQUALS,
        requiresValue: true,
        icon: ICONS.notEquals
      },
      [FilterOperation.CONTAINS]: {
        operation: FilterOperation.CONTAINS,
        requiresValue: true,
        icon: ICONS.textAa
      },
      [FilterOperation.NOT_CONTAINS]: {
        operation: FilterOperation.NOT_CONTAINS,
        requiresValue: true,
        icon: ICONS.textAa
      },
      [FilterOperation.STARTS_WITH]: {
        operation: FilterOperation.STARTS_WITH,
        requiresValue: true,
        icon: ICONS.textAa
      },
      [FilterOperation.ENDS_WITH]: {
        operation: FilterOperation.ENDS_WITH,
        requiresValue: true,
        icon: ICONS.textAa
      },
      [FilterOperation.IS_EMPTY]: {
        operation: FilterOperation.IS_EMPTY,
        requiresValue: false,
        icon: ICONS.empty
      },
      [FilterOperation.IS_NOT_EMPTY]: {
        operation: FilterOperation.IS_NOT_EMPTY,
        requiresValue: false,
        icon: ICONS.empty
      },
      [FilterOperation.MATCHES_REGEX]: {
        operation: FilterOperation.MATCHES_REGEX,
        requiresValue: true,
        icon: ICONS.code
      },

      // Number/Comparison operations
      [FilterOperation.GREATER_THAN]: {
        operation: FilterOperation.GREATER_THAN,
        requiresValue: true,
        icon: ICONS.greaterThan
      },
      [FilterOperation.GREATER_THAN_OR_EQUALS]: {
        operation: FilterOperation.GREATER_THAN_OR_EQUALS,
        requiresValue: true,
        icon: ICONS.greaterThanOrEqual
      },
      [FilterOperation.LESS_THAN]: {
        operation: FilterOperation.LESS_THAN,
        requiresValue: true,
        icon: ICONS.lessThan
      },
      [FilterOperation.LESS_THAN_OR_EQUALS]: {
        operation: FilterOperation.LESS_THAN_OR_EQUALS,
        requiresValue: true,
        icon: ICONS.lessThanOrEqual
      },
      [FilterOperation.BETWEEN]: {
        operation: FilterOperation.BETWEEN,
        requiresValue: true,
        requiresTwoValues: true,
        icon: ICONS.arrowsHorizontal
      },

      // Select operations
      [FilterOperation.IS]: {
        operation: FilterOperation.IS,
        requiresValue: true,
        icon: ICONS.equals
      },
      [FilterOperation.IS_NOT]: {
        operation: FilterOperation.IS_NOT,
        requiresValue: true,
        icon: ICONS.notEquals
      },
      [FilterOperation.IS_ANY_OF]: {
        operation: FilterOperation.IS_ANY_OF,
        requiresValue: true,
        supportsMultipleValues: true,
        icon: ICONS.listBullets
      },
      [FilterOperation.IS_NONE_OF]: {
        operation: FilterOperation.IS_NONE_OF,
        requiresValue: true,
        supportsMultipleValues: true,
        icon: ICONS.listBullets
      },

      // Boolean operations
      [FilterOperation.IS_TRUE]: {
        operation: FilterOperation.IS_TRUE,
        requiresValue: false,
        icon: ICONS.checkCircle
      },
      [FilterOperation.IS_FALSE]: {
        operation: FilterOperation.IS_FALSE,
        requiresValue: false,
        icon: ICONS.xCircle
      },

      // Date operations
      [FilterOperation.IS_BEFORE]: {
        operation: FilterOperation.IS_BEFORE,
        requiresValue: true,
        icon: ICONS.caretLeft
      },
      [FilterOperation.IS_AFTER]: {
        operation: FilterOperation.IS_AFTER,
        requiresValue: true,
        icon: ICONS.caretRight
      },
      [FilterOperation.IS_ON_OR_BEFORE]: {
        operation: FilterOperation.IS_ON_OR_BEFORE,
        requiresValue: true,
        icon: ICONS.caretLeft
      },
      [FilterOperation.IS_ON_OR_AFTER]: {
        operation: FilterOperation.IS_ON_OR_AFTER,
        requiresValue: true,
        icon: ICONS.caretRight
      },
      [FilterOperation.IS_WITHIN]: {
        operation: FilterOperation.IS_WITHIN,
        requiresValue: true,
        icon: ICONS.calendarBlank
      },
      [FilterOperation.IS_RELATIVE]: {
        operation: FilterOperation.IS_RELATIVE,
        requiresValue: true,
        icon: ICONS.calendarBlank
      },

      // Label operations
      [FilterOperation.LABEL_EQUALS]: {
        operation: FilterOperation.LABEL_EQUALS,
        requiresValue: true,
        labelNeed: 'value',
        icon: ICONS.equals
      },
      [FilterOperation.LABEL_NOT_EQUALS]: {
        operation: FilterOperation.LABEL_NOT_EQUALS,
        requiresValue: true,
        labelNeed: 'value',
        icon: ICONS.notEquals
      },
      [FilterOperation.LABEL_IN]: {
        operation: FilterOperation.LABEL_IN,
        requiresValue: true,
        supportsMultipleValues: true,
        labelNeed: 'value',
        icon: ICONS.listBullets
      },
      [FilterOperation.LABEL_NOT_IN]: {
        operation: FilterOperation.LABEL_NOT_IN,
        requiresValue: true,
        supportsMultipleValues: true,
        labelNeed: 'value',
        icon: ICONS.listBullets
      },
      [FilterOperation.HAS_LABEL]: {
        operation: FilterOperation.HAS_LABEL,
        requiresValue: false,
        labelNeed: 'none',
        icon: ICONS.tag
      },
      [FilterOperation.NOT_HAS_LABEL]: {
        operation: FilterOperation.NOT_HAS_LABEL,
        requiresValue: false,
        labelNeed: 'none',
        icon: ICONS.prohibit
      }
    };

    const config = configs[operation];
    return {
      ...config,
      label: this.translate.instant(`filters.operations.${operation.toLowerCase()}`)
    };
  }

  /**
   * Get all operation configs for a data type
   */
  getOperationConfigsForDataType(dataType: FilterDataType): FilterOperationConfig[] {
    const operations = this.getOperationsForDataType(dataType);
    return operations.map((op) => this.getOperationConfig(op));
  }

  /**
   * Get icon for data type
   */
  getDataTypeIcon(dataType: FilterDataType): string {
    const iconMap: Record<FilterDataType, string> = {
      [FilterDataType.TEXT]: ICONS.textAa,
      [FilterDataType.MULTILINE_TEXT]: ICONS.textAlignLeft,
      [FilterDataType.NUMBER]: ICONS.binary,
      [FilterDataType.DATE]: ICONS.calendarBlank,
      [FilterDataType.SELECT]: ICONS.listBullets,
      [FilterDataType.MULTI_SELECT]: ICONS.listChecks,
      [FilterDataType.UID]: ICONS.signature,
      [FilterDataType.UID_LIST]: ICONS.listBullets,
      [FilterDataType.BOOLEAN]: ICONS.toggleRight,
      [FilterDataType.LINK]: ICONS.link,
      [FilterDataType.LABELS]: ICONS.tag,
      [FilterDataType.TAGS]: ICONS.tag,
      [FilterDataType.DURATION]: ICONS.clock
    };
    return iconMap[dataType] || ICONS.textAa;
  }

  /**
   * Check if operation requires a value input
   */
  requiresValue(operation: FilterOperation): boolean {
    return this.getOperationConfig(operation).requiresValue;
  }

  /**
   * Check if operation requires two values (like BETWEEN)
   */
  requiresTwoValues(operation: FilterOperation): boolean {
    return this.getOperationConfig(operation).requiresTwoValues || false;
  }

  /**
   * Check if operation supports multiple values
   */
  supportsMultipleValues(operation: FilterOperation): boolean {
    return this.getOperationConfig(operation).supportsMultipleValues || false;
  }
}
